import { Fragment, Suspense, useEffect, useState } from "react";
import type { ReactElement } from "react";
import {
  BrowserRouter,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useViewport } from "./lib/useViewport";
import { ChatView } from "./views/ChatView";
import { OverviewView } from "./views/OverviewView";
import { StackView } from "./views/StackView";
import { DownloadsView } from "./views/DownloadsView";
import { DecideView } from "./views/DecideView";
import { SettingsView } from "./views/SettingsView";
import { DebugView } from "./views/DebugView";
import "./styles/fonts.css";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/chat.css";
import "./styles/shell.css";
import "./styles/status.css";
import { StatusProvider, useSharedStatus } from "./lib/StatusContext";
import { useChatNavTo } from "./lib/lastChat";
import { apiFetch, onUnauthorized } from "./lib/api";
import { LoginView } from "./views/LoginView";
import "./styles/login.css";

type Tab = { to: string; label: string; icon: () => ReactElement; desk?: boolean };

const GROUPS: { label: string; items: Tab[] }[] = [
  {
    label: "Stack",
    items: [
      { to: "/overview", label: "Overview", icon: OverviewIcon },
      { to: "/stack", label: "Stack", icon: StackIcon },
      { to: "/downloads", label: "Queue", icon: DownloadsIcon },
      { to: "/decide", label: "Decide", icon: DecideIcon },
    ],
  },
  {
    label: "Agent",
    items: [{ to: "/chat", label: "Chat", icon: ChatIcon, desk: true }],
  },
];

/* Settings is pinned to the sidebar's foot. On mobile the five slots are
   Overview · Stack · FAB · Queue · Decide. Chat is the centre FAB on the
   phone and a sidebar item on desktop; Settings stays desktop-only here
   and is the topbar gear on mobile. */
const FOOT: Tab[] = [{ to: "/settings", label: "Settings", icon: SettingsIcon, desk: true }];

function Nav() {
  const { pathname } = useLocation();
  const { data } = useSharedStatus();
  const chatTo = useChatNavTo();

  // Alerts live on Overview; stuck imports sit on Decide. A badge points at
  // the screen that can actually clear it.
  const faults = data?.alerts.filter((a) => a.level === "fault").length ?? 0;
  const badges: Record<string, number> = {
    "/overview": faults,
    "/decide": data?.pendingCount ?? 0,
  };

  const chatActive = pathname === "/chat" || pathname.startsWith("/chat/");

  const item = ({ to, label, icon: Icon, desk }: Tab) => {
    // /chat/:id must still light up the Chat item (desktop sidebar).
    const active = to === "/chat" ? chatActive : pathname === to || pathname.startsWith(`${to}/`);
    const badge = badges[to] ?? 0;
    return (
      <NavLink
        key={to}
        to={to === "/chat" ? chatTo : to}
        className={`nav__item${desk ? " nav__item--desk" : ""}${active ? " is-active" : ""}`}
        aria-label={label}
        aria-current={active ? "page" : undefined}
      >
        <Icon />
        <span className="nav__label">{label}</span>
        {badge > 0 && (
          <span className="nav__badge" aria-label={`${badge} needing attention`}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </NavLink>
    );
  };

  // Worst-of rollups for the sidebar's status legend.
  const worstVol = data?.volumes.some((v) => v.level === "fault")
    ? "fault"
    : data?.volumes.some((v) => v.level === "warn")
      ? "warn"
      : "ok";
  const svcDown = data?.services.filter((x) => !x.ok).length ?? 0;
  const hostsDown = data?.fleet.filter((h) => h.ok === false).length ?? 0;

  return (
    <nav className="nav" aria-label="Primary">
      <div className="nav__brand">
        <span className="nav__mark" aria-hidden="true">
          LC
        </span>
        <div className="grow truncate">
          <div className="nav__brandName">Lab Console</div>
          <div className="nav__brandSub">10.1.0.10</div>
        </div>
      </div>

      <div className="nav__scroll">
        {GROUPS.map((g) => (
          <div className="nav__group" key={g.label}>
            <div className="nav__groupLabel">{g.label}</div>
            {g.items.map((tab) => (
              <Fragment key={tab.to}>
                {item(tab)}
                {tab.to === "/stack" && (
                  <NavLink
                    to={chatTo}
                    className={`nav__fab${chatActive ? " is-active" : ""}`}
                    aria-label="Chat"
                    title="Chat"
                    aria-current={chatActive ? "page" : undefined}
                  >
                    <span>
                      <ChatIcon />
                    </span>
                  </NavLink>
                )}
              </Fragment>
            ))}
          </div>
        ))}

        {data && (
          <div className="nav__status" aria-label="Stack health">
            <div className="nav__groupLabel">Health</div>
            <div className="nav__statusRow">
              <span className={`dot dot--${worstVol}`} />
              Storage
              <span>{data.volumes.length}</span>
            </div>
            <div className="nav__statusRow">
              <span className={`dot ${svcDown ? "dot--fault" : "dot--ok"}`} />
              Services
              <span>{svcDown ? `${svcDown} down` : "all up"}</span>
            </div>
            <div className="nav__statusRow">
              <span className={`dot ${hostsDown ? "dot--fault" : "dot--ok"}`} />
              Hardware
              <span>{hostsDown ? `${hostsDown} down` : `${data.fleet.length} up`}</span>
            </div>
          </div>
        )}
      </div>

      <div className="nav__foot">
        {FOOT.map(item)}
        <LiveChip ok={data?.ok} />
      </div>
    </nav>
  );
}

function LiveChip({ ok }: { ok?: boolean }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString(undefined, { hour12: false });
  return (
    <div className="nav__live">
      <span className={`dot ${ok === false ? "dot--fault" : "dot--ok dot--live"}`} />
      live · <time dateTime={now.toISOString()}>{time}</time>
    </div>
  );
}

function StatusRedirect() {
  const { hash } = useLocation();
  return <Navigate to={`/stack${hash}`} replace />;
}

function Shell() {
  useViewport();
  const { pathname } = useLocation();
  /* data-locked still gates chat-only composer pinning in chat.css.
     The locked 100svh shell itself is now the only layout, on every route. */
  const chatLocked = pathname === "/chat" || pathname.startsWith("/chat/");
  const [intro, setIntro] = useState(
    "Ask about disks, downloads, stuck imports, or why something is missing.",
  );

  useEffect(() => {
    // Chat intro and window title are house copy from the server config.
    apiFetch("/api/ui")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { ui?: { chatIntro?: string; title?: string } } | null) => {
        if (j?.ui?.chatIntro) setIntro(j.ui.chatIntro);
        if (j?.ui?.title) document.title = j.ui.title;
      })
      .catch(() => undefined);
  }, []);

  return (
    <div className="shell" data-locked={chatLocked ? "true" : undefined}>
      <Nav />
      <main className="main">
        <Suspense fallback={<div className="empty">Loading…</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewView />} />
            <Route path="/stack" element={<StackView />} />
            <Route path="/downloads" element={<DownloadsView />} />
            <Route path="/status" element={<StatusRedirect />} />
            <Route path="/chat" element={<ChatView intro={intro} />} />
            <Route path="/chat/:id" element={<ChatView intro={intro} />} />
            <Route path="/history" element={<Navigate to="/chat" replace />} />
            <Route path="/decide" element={<DecideView />} />
            <Route path="/settings" element={<SettingsView />} />
            <Route path="/debug" element={<DebugView />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState<"loading" | "in" | "out">("loading");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/status", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) return false;
        const j = (await r.json()) as { authenticated?: boolean };
        return j.authenticated === true;
      })
      .then((ok) => {
        if (!cancelled) setAuth(ok ? "in" : "out");
      })
      .catch(() => {
        if (!cancelled) setAuth("out");
      });
    const off = onUnauthorized(() => setAuth("out"));
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return (
    <BrowserRouter>
      {auth === "out" ? (
        <LoginView title="Lab Console" onSuccess={() => setAuth("in")} />
      ) : auth === "in" ? (
        <StatusProvider>
          <Shell />
        </StatusProvider>
      ) : null}
    </BrowserRouter>
  );
}

/* ------------------------------------------------------------------ icons */

const S = {
  viewBox: "0 0 24 24",
  width: 21,
  height: 21,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function OverviewIcon() {
  return (
    <svg {...S}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.4" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.4" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.4" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.4" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg {...S}>
      <path d="M20 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function DownloadsIcon() {
  return (
    <svg {...S}>
      <path d="M12 4v11m0 0 4-4m-4 4-4-4" />
      <path d="M5 19h14" />
    </svg>
  );
}
function StackIcon() {
  return (
    <svg {...S}>
      <rect x="3" y="4" width="18" height="5" rx="1.5" />
      <rect x="3" y="15" width="18" height="5" rx="1.5" />
      <path d="M7 6.5h.01M7 17.5h.01" />
    </svg>
  );
}
function DecideIcon() {
  return (
    <svg {...S}>
      <path d="M9 11.5 11 13.5 15.5 9" />
      <path d="M20 6.5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.6 1.6 0 0 0 15 19.4a1.6 1.6 0 0 0-1 1.47V21a2 2 0 1 1-4 0v-.09A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.6 1.6 0 0 0 4.6 15a1.6 1.6 0 0 0-1.47-1H3a2 2 0 1 1 0-4h.09A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.6 1.6 0 0 0 9 4.6a1.6 1.6 0 0 0 1-1.47V3a2 2 0 1 1 4 0v.09A1.6 1.6 0 0 0 15 4.6a1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.6 1.6 0 0 0 19.4 9a1.6 1.6 0 0 0 1.47 1H21a2 2 0 1 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1z" />
    </svg>
  );
}

